import type { ReactNode } from 'react';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import type { PredefinedPrompt, PredefinedPromptsContextType, PredefinedPromptsResponse } from '../types';

import { get } from '~/utils/request';

const PredefinedPromptsContext = createContext<PredefinedPromptsContextType | undefined>(undefined);

export const usePredefinedPromptsContext = () => {
  const ctx = useContext(PredefinedPromptsContext);
  if (!ctx) throw new Error('usePredefinedPromptsContext must be used within a PredefinedPromptsProvider');
  return ctx;
};

interface PredefinedPromptsProviderProps {
  children: ReactNode;
}

export const PredefinedPromptsProvider: React.FC<PredefinedPromptsProviderProps> = ({ children }) => {
  const [prompts, setPrompts] = useState<PredefinedPrompt[]>([]);
  const [isPromptsLoading, setIsPromptsLoading] = useState(false);
  const [promptsError, setPromptsError] = useState<string | null>(null);

  const fetchPrompts = useCallback(async () => {
    setIsPromptsLoading(true);
    setPromptsError(null);
    try {
      const response = await get<PredefinedPromptsResponse>({
        url: '/api/v1/predefined_prompts',
      });
      setPrompts(response.prompts || []);
      console.log(`[PredefinedPrompts] Fetched ${response.prompts?.length || 0} prompts`);
    } catch (error) {
      setPromptsError('Failed to fetch predefined prompts.');
      console.error('Error fetching predefined prompts:', error);
    } finally {
      setIsPromptsLoading(false);
    }
  }, []);

  // Auto-fetch prompts when provider mounts
  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  return (
    <PredefinedPromptsContext.Provider value={{ prompts, isPromptsLoading, promptsError, fetchPrompts }}>
      {children}
    </PredefinedPromptsContext.Provider>
  );
};
